import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Plus, Minus, ShieldCheck, Tag, AtSign } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { getCart, updateQty, removeFromCart, cartSubtotal, updateAccountEmail } from "../lib/cart";
import TurnstileWidget from "../components/TurnstileWidget";

export default function Checkout() {
  const [cart, setCart] = useState(getCart());
  const [email, setEmail] = useState("");
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [publicCfg, setPublicCfg] = useState({ turnstile_active: false, turnstile_site_key: "" });

  useEffect(() => {
    const upd = () => setCart(getCart());
    window.addEventListener("cart_updated", upd);
    api.get("/settings/public").then((r) => setPublicCfg(r.data)).catch(() => {});
    return () => window.removeEventListener("cart_updated", upd);
  }, []);

  const subtotal = cartSubtotal();
  const total = Math.max(0, subtotal - discount);

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const r = await api.post("/coupons/validate", { code: coupon.trim().toUpperCase(), subtotal });
      setAppliedCoupon(r.data);
      setDiscount(r.data.discount);
      toast.success(`Coupon ${r.data.code} applied (-$${r.data.discount.toFixed(2)})`);
    } catch (e) {
      toast.error("Invalid or expired coupon");
      setAppliedCoupon(null);
      setDiscount(0);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setDiscount(0);
    setCoupon("");
  };

  const submit = async () => {
    if (!email) { toast.error("Email is required"); return; }
    if (cart.length === 0) { toast.error("Cart is empty"); return; }
    // Validate account_email for subscription items
    for (const it of cart) {
      if (it.requires_account_email && !it.account_email) {
        toast.error(`Please provide your Autodesk account email for ${it.name}`);
        return;
      }
    }
    if (publicCfg.turnstile_active && publicCfg.turnstile_site_key && !turnstileToken) {
      toast.error("Please complete the captcha");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/checkout/session", {
        email,
        items: cart.map((c) => ({
          product_id: c.product_id,
          quantity: c.quantity,
          account_email: c.account_email || null,
        })),
        coupon_code: appliedCoupon?.code || null,
        origin_url: window.location.origin,
        turnstile_token: turnstileToken || null,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div data-testid="checkout-empty" className="max-w-3xl mx-auto px-6 py-20 text-center">
        <h1 className="font-heading text-3xl font-bold">Your cart is empty</h1>
        <p className="text-[#A1A1AA] mt-2">Add some software to get started.</p>
        <Link to="/products" className="btn-primary mt-6 inline-flex">Browse catalog</Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="max-w-6xl mx-auto px-6 py-12">
      <div className="font-mono text-xs uppercase tracking-widest text-[#00FF66]">// checkout</div>
      <h1 className="font-heading font-bold text-4xl mt-2">Secure checkout</h1>

      <div className="grid lg:grid-cols-3 gap-8 mt-10">
        <div className="lg:col-span-2 space-y-6">
          <div className="surface p-6">
            <h2 className="font-heading font-semibold text-lg mb-4">Cart items</h2>
            <div className="space-y-4">
              {cart.map((it) => (
                <div key={it.product_id} className="pb-4 border-b border-[#1E293B] last:border-0 last:pb-0">
                  <div className="flex gap-4">
                    <div className="w-16 h-16 rounded-md bg-[#0a0a0f] overflow-hidden shrink-0">
                      {it.image ? <img src={it.image} alt="" className="w-full h-full object-cover" /> : <div className="bg-grid w-full h-full" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{it.name}</div>
                      <div className="font-mono text-[#00FF66] text-sm mt-0.5">
                        ${it.price.toFixed(2)}
                        {it.subscription_period && <span className="text-[#A1A1AA] text-xs uppercase ml-1">/{it.subscription_period}</span>}
                      </div>
                    </div>
                    <div className="flex items-center surface h-9">
                      <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="px-2 hover:text-[#00FF66]"><Minus className="w-3 h-3" /></button>
                      <span className="font-mono text-sm w-8 text-center">{it.quantity}</span>
                      <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="px-2 hover:text-[#00FF66]"><Plus className="w-3 h-3" /></button>
                    </div>
                    <button onClick={() => removeFromCart(it.product_id)} className="text-[#71717A] hover:text-[#FF3B30]" data-testid={`remove-${it.product_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {it.requires_account_email && (
                    <div className="mt-3 ml-20 p-3 rounded-md border border-[#1F8FE8]/30 bg-[#1F8FE8]/5">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#5C9CD8] flex items-center gap-1 mb-2">
                        <AtSign className="w-3 h-3" /> autodesk account email for activation
                      </div>
                      <input
                        data-testid={`autodesk-email-${it.product_id}`}
                        type="email"
                        value={it.account_email || ""}
                        onChange={(e) => updateAccountEmail(it.product_id, e.target.value)}
                        placeholder="your-autodesk-account@example.com"
                        className="input-term"
                      />
                      <p className="text-[10px] font-mono text-[#71717A] mt-1.5">we'll activate the {it.subscription_period || "1 year"} subscription on this Autodesk account.</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="surface p-6">
            <h2 className="font-heading font-semibold text-lg mb-4">Delivery email</h2>
            <input
              data-testid="checkout-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="input-term"
            />
            <p className="text-xs text-[#71717A] mt-2 font-mono">your license keys will be sent here · also visible on success page</p>
          </div>

          <div className="surface p-6">
            <h2 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2"><Tag className="w-4 h-4 text-[#00FF66]" />Coupon code</h2>
            {appliedCoupon ? (
              <div className="flex items-center justify-between bg-[#00FF66]/10 border border-[#00FF66]/30 px-4 py-3 rounded-md">
                <div className="font-mono text-sm">{appliedCoupon.code} <span className="text-[#A1A1AA]">— saved ${appliedCoupon.discount.toFixed(2)}</span></div>
                <button onClick={removeCoupon} className="text-xs text-[#A1A1AA] hover:text-[#FF3B30]">remove</button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input data-testid="coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="WELCOME10" className="input-term" />
                <button data-testid="apply-coupon-btn" onClick={applyCoupon} className="btn-ghost shrink-0">apply</button>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="surface p-6 sticky top-20">
            <h2 className="font-heading font-semibold text-lg">Order summary</h2>
            <div className="mt-5 space-y-2 text-sm font-mono">
              <div className="flex justify-between"><span className="text-[#A1A1AA]">subtotal</span><span>${subtotal.toFixed(2)}</span></div>
              {discount > 0 && <div className="flex justify-between text-[#00FF66]"><span>discount</span><span>-${discount.toFixed(2)}</span></div>}
              <div className="border-t border-[#1E293B] pt-3 mt-3 flex justify-between text-base">
                <span>total</span>
                <span data-testid="checkout-total" className="text-[#00FF66] font-bold">${total.toFixed(2)}</span>
              </div>
            </div>
            <button data-testid="pay-btn" onClick={submit} disabled={loading || (publicCfg.turnstile_active && publicCfg.turnstile_site_key && !turnstileToken)} className="btn-primary mt-6 w-full justify-center">
              {loading ? "redirecting..." : `pay $${total.toFixed(2)}`}
            </button>
            {publicCfg.turnstile_active && publicCfg.turnstile_site_key && (
              <div className="mt-4">
                <TurnstileWidget siteKey={publicCfg.turnstile_site_key} onToken={setTurnstileToken} theme="dark" />
              </div>
            )}
            <div className="flex items-center gap-2 mt-4 text-xs text-[#A1A1AA] font-mono">
              <ShieldCheck className="w-3.5 h-3.5 text-[#00FF66]" />
              encrypted via stripe · cards & wallets
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
